<script lang="ts">
  import { page } from '$app/stores';
  import { LayoutPreferenceType, type LayoutPreference } from '$types/races';
  import { isRaceClosed, type bookmakerOverroundStore } from '$utils/race';
  import { createTooltip, melt } from '@melt-ui/svelte';
  import { fade } from 'svelte/transition';
  import { currentEventData } from './TextInputCell.svelte';
  import { feedBookmakers } from '$utils/bookmakers';

  $: ({ race, streamed } = $page.data);

  export let overround: ReturnType<typeof bookmakerOverroundStore> | undefined =
    undefined;
  export let layoutPreference: LayoutPreference;

  $: isWarn =
    overround &&
    $overround &&
    $overround < 100 &&
    $overround > 0 &&
    overround.hasPrices();
  $: lastUpdated_ = streamed?.raceData?.data?.race.links?.find(
    (l) => l.bookmaker === layoutPreference.bookmaker
  )?.lastSuccessfulPriceUpdate;
  $: lastUpdated = lastUpdated_ ? new Date(lastUpdated_) : null;

  // hasn't been updated in 2 hours
  $: isStale =
    $race &&
    !isRaceClosed($race) &&
    lastUpdated &&
    overround &&
    $overround &&
    overround.hasPrices() &&
    lastUpdated.getTime() < Date.now() - 1000 * 60 * 60 * 2;

  $: warn = isStale ? '⚠️ ' : isWarn ? '⚠️ ' : '';
  $: warnMessage =
    isStale &&
    layoutPreference.bookmaker &&
    !feedBookmakers.includes(layoutPreference.bookmaker)
      ? 'This market has not been updated in a while, there may be a problem!'
      : isWarn
        ? 'This bookmaker may not have priced in recent scratchings!'
        : '';

  // $: userEventData = streamed?.userEventData?.data?.userEventData.data ?? null;
  // $: activeRunners = $race?.runners?.filter((runner) => !runner.scratchedTime);
  let userOverround: number | string = 0;
  $: currentData = $currentEventData?.find(
    (d) => d.columnName === layoutPreference.name
  )?.selectionData;
  $: {
    if (layoutPreference.type === LayoutPreferenceType.TEXT) {
      if (currentData) {
        // check if all of the userEventData values are valid numbers (cast to number from string), while catching errors
        const userOverrounds = Object.values(currentData).map((d) => {
          try {
            return Number(d.value);
          } catch (e) {
            return null;
          }
        });
        // check if the userOverrounds array contains any null values
        if (userOverrounds.some((value) => value === null || !value)) {
          userOverround = 0;
        } else {
          // check if the userOverrounds array has a length equal to the amount of active runners
          const activeRunners = $race?.runners?.filter(
            (runner) => !runner.scratchedTime
          );
          if (activeRunners && userOverrounds.length === activeRunners.length) {
            // sum the inverses of the userOverrounds array and multiply by 100

            userOverround =
              // @ts-expect-error userOverrounds does not contain null values
              (100 * userOverrounds.reduce((a, b) => a + 1 / b, 0)).toFixed(0);
          }
        }
      }
    }
  }

  const {
    elements: { trigger, content, arrow },
    states: { open }
  } = createTooltip({
    positioning: {
      placement: 'top'
    },
    group: true
  });
</script>

{#if $open}
  <div
    class="z-10 flex flex-col items-center rounded-lg bg-slate-900 text-sm text-gray-200 shadow"
    use:melt={$content}
    transition:fade={{ duration: 100 }}
  >
    <div use:melt={$arrow} />
    <div class="flex flex-col items-center px-2 py-1">
      {warn}Updated: {lastUpdated?.toLocaleString('en-AU')}
      <span class="text-xs text-gray-300">
        {warnMessage}
      </span>
    </div>
  </div>
{/if}

{#if $overround || userOverround}
  <td
    use:melt={$trigger}
    class="z-50 border px-1 py-1 font-medium text-gray-600 {isWarn &&
      'bg-red-200 font-bold text-black'}
   {isStale && 'bg-yellow-200 font-bold text-black'}"
  >
    {$overround || userOverround}%
  </td>
{:else}
  <td />
{/if}
